// AuthContext.jsx — session state from the dc_session cookie via GET /api/me.
// The frontend holds no token; it only knows the account object the backend
// returns (or null when the /api/me probe 401s). refresh() re-probes after a
// successful login; logout() revokes server-side then clears local state.
import { createContext, useContext, useState, useEffect, useCallback } from 'react';
import api from '../api/client.mjs';
import { pickCurrent, readStoredGroupId, storeGroupId } from '../api/groups.mjs';

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [account, setAccount] = useState(null);
  const [loading, setLoading] = useState(true);
  // ── 第一百零一批（多项目制）：组上下文与会话同源 ──────────────────────────
  // /api/me 现在随账号带「我的项目组」（成员=关系表行，管理员=全部未归档组），
  // 组列表与当前组因此不需要第二次往返、也没有第二个会漂移的取数面。
  // 当前组持久在 localStorage（pickCurrent 决定回落次序），单组成员永远只有
  // 一项——切换器由 groups.length 决定显隐，不需要任何专门的"单组模式"。
  const [groups, setGroups] = useState([]);
  const [currentGroup, setCurrentGroupState] = useState(null);
  // ── SB-146（第一百二十二批）：本会话是怎么登进来的 ───────────────────────
  // /api/me 回 sessionOrigin（'login_link' = 邮件直登；'code'/'password'/null = 普通
  // 登录，旧会话行 NULL 视同普通）。只有 'login_link' 会触发直登清欠链
  // （ForcedReadGate 的清欠段）。失败方向：字段缺席/老后端 ⇒ null ⇒ 不清欠——
  // 宁可少清一次，绝不把普通登录当直登拦。
  const [sessionOrigin, setSessionOrigin] = useState(null);

  // Probe the session. 401 (UNAUTHORIZED) and any error ⇒ signed-out (null);
  // always resolves `loading` to false so guards can render.
  const refresh = useCallback(async () => {
    try {
      const data = await api.me();
      const acct = data?.account ?? null;
      const gs = Array.isArray(data?.groups) ? data.groups : [];
      setAccount(acct);
      setGroups(gs);
      setCurrentGroupState(pickCurrent(gs, readStoredGroupId()));
      // 顶层优先、account 块兜底：契约只说「/api/me 回 sessionOrigin」，E 轨并行
      // 落地——两个合理落点都读，读不到就是 null（不清欠）。非字符串一律不收。
      const so = data?.sessionOrigin ?? data?.account?.sessionOrigin ?? null;
      setSessionOrigin(typeof so === 'string' && so ? so : null);
      return acct;
    } catch {
      setAccount(null);
      setGroups([]);
      setCurrentGroupState(null);
      setSessionOrigin(null);
      return null;
    } finally {
      setLoading(false);
    }
  }, []);

  const setCurrentGroup = useCallback((g) => {
    if (!g || g.id == null) return;
    storeGroupId(g.id);
    setCurrentGroupState(g);
  }, []);

  const logout = useCallback(async () => {
    try {
      await api.logout();
    } catch {
      /* logout is idempotent server-side; clear local state regardless */
    }
    setAccount(null);
    setGroups([]);
    setCurrentGroupState(null);
    setSessionOrigin(null);
  }, []);

  // One probe on mount.
  useEffect(() => {
    refresh();
  }, [refresh]);

  const value = {
    account, loading, refresh, logout, groups, currentGroup, setCurrentGroup, sessionOrigin,
  };
  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within an AuthProvider');
  return ctx;
}
